import {
	Box,
	Button,
	Chip,
	CircularProgress,
	IconButton,
	Paper,
	Skeleton,
	Typography,
} from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import queryString from 'query-string';
import { CloudDownload, CloudUpload, Download, KeyboardBackspace } from '@mui/icons-material';
import { tableStructure } from '../EUDR/tableStructure';
import {
	getDownloadEudrLastInvoiceLoading,
	getEudrActivateLoading,
	getEudrItems,
	getEudrItemsLoading,
	getGenerateInvoiceEudrLoading,
} from '../../redux/reducers/eudr/selectors';
import {
	activateEudrRequest,
	downloadEudrLastInvoiceRequest,
	generateInvoiceEudrRequest,
	getEudrItemsRequest,
} from '../../redux/reducers/eudr/reducer';
import { PAGE_SIZE } from '../../services/constants/env';
import { TEudrItem } from '../../redux/reducers/eudr/types';
import { PATHS } from '../../routes/constants';
import { EasFullDataComponent } from '../../ui/EasFullDataComponent';
import { ConfirmationDialog } from '../../ui/ConfirmationDialog';
import { AddAttachmentDialog } from '../EMManagement/EMManagementId/EMManagementIdBody/EmAttachments/AddAttachmentDialog';
import { ButtonCircularProgress } from '../../ui/ButtonCircularProgress';
import { getEmAttachmentUploadLoading } from '../../redux/reducers/attachments/selectors';

export const EUDRUser: FC = () => {
	const dispatch = useDispatch();
	const { emId } = useParams<{ emId: string }>();
	const eudrItems = useSelector(getEudrItems);
	const eudrItemsLoading = useSelector(getEudrItemsLoading);
	const generateInvoiceEudrLoading = useSelector(getGenerateInvoiceEudrLoading);
	const downloadEudrLastInvoiceLoading = useSelector(getDownloadEudrLastInvoiceLoading);
	const activateEudrLoading = useSelector(getEudrActivateLoading);
	const uploadEudrContractLoading = useSelector(getEmAttachmentUploadLoading);
	const navigate = useNavigate();
	const { search } = useLocation();
	const navigateParams = queryString.parse(search);
	const { page } = queryString.parse(search);
	const [openActivateAccountDialog, setOpenActivateAccountDialog] = useState(false);
	const [openAddAttachmentDialog, setOpenAddAttachmentDialog] = useState(false);
	const handleOpenActivateAccountDialog = () => {
		setOpenActivateAccountDialog(true);
	};

	const handleCloseActivateAccountDialog = () => {
		setOpenActivateAccountDialog(false);
	};

	const setCloseAddAttachmentDialog = () => {
		setOpenAddAttachmentDialog(false);
	};

	useEffect(() => {
		const filters = [];
		if (emId) {
			filters.push({
				fieldName: 'emId',
				value: emId,
				operator: '=',
			});
		}

		dispatch(
			getEudrItemsRequest({
				pageNumber: page ? +page : 1,
				pageSize: PAGE_SIZE,
				searchString: String(navigateParams?.search ?? ''),
				filters,
			}),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, search, emId]);

	const handleActivateAccount = () => {
		if (!emId) return;
		if (!eudrItems?.data?.[0]?.id) return;
		dispatch(activateEudrRequest({ id: eudrItems?.data?.[0]?.id ?? 0 }));
		handleCloseActivateAccountDialog();
	};

	const handleDownloadContract = () => {
		// if (!eudrItems?.data?.[0]?.id) return;
		// dispatch(
		// 	downloadEmAttachmentRequest({
		// 		emId: Number(emId),
		// 		attachmentId: id,
		// 		fileName: row.fileName,
		// 	}),
		// );
	};

	const handleGenerateInvoice = () => {
		if (!eudrItems?.data?.[0]?.id) return;
		dispatch(generateInvoiceEudrRequest({ id: eudrItems?.data?.[0]?.id ?? 0 }));
	};

	const handleDownloadInvoice = () => {
		if (!eudrItems?.data?.[0]?.id) return;
		dispatch(downloadEudrLastInvoiceRequest({ id: eudrItems?.data?.[0]?.id ?? 0 }));
	};

	return (
		<Box>
			{eudrItems?.data?.[0] && (
				<EasFullDataComponent<TEudrItem>
					tableStructure={tableStructure}
					data={eudrItems?.data?.[0] ?? null}
					itemsIsLoading={eudrItemsLoading}
					header={
						<Box
							sx={{
								backgroundColor: 'primary.light',
								p: '4px 4px 4px 8px',
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center',
								gap: '10px',
								width: '100%',
							}}
						>
							<Box
								sx={{
									backgroundColor: 'primary.light',
									display: 'flex',
									justifyContent: 'start',
									alignItems: 'center',
									gap: '10px',
									cursor: 'pointer',
								}}
								onClick={() => {
									navigate(PATHS.EUDR_PAGE);
								}}
							>
								<IconButton
									color="primary"
									sx={{
										p: '0',
									}}
								>
									<KeyboardBackspace />
								</IconButton>
								<Typography
									sx={{
										fontSize: '.9rem',
										color: 'primary.main',
										fontWeight: 'bold',
									}}
								>
									EUDR
								</Typography>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', ml: '10px' }}>
									<Typography
										sx={{
											fontSize: '.8rem',
											color: 'primary.main',
										}}
									>
										Service Status:
									</Typography>
									<Chip
										label={eudrItems?.data?.[0]?.serviceStatusName}
										size="small"
										sx={{
											fontSize: '.7rem',
											color: '#00000080',
											height: '20px',
											fontWeight: 'bold',
											backgroundColor:
												tableStructure
													?.find((i) => i.selector === 'serviceStatusName')
													?.colors?.find(
														(i) => i[0] === eudrItems?.data?.[0]?.serviceStatusName,
													)?.[1] ?? 'primary.main',
										}}
									/>
								</Box>
							</Box>
							<Box
								sx={{
									backgroundColor: 'primary.light',
									display: 'flex',
									justifyContent: 'start',
									alignItems: 'center',
									gap: '4px',
									cursor: 'pointer',
								}}
							>
								<Button
									variant="contained"
									size="small"
									onClick={handleGenerateInvoice}
									disabled={generateInvoiceEudrLoading}
								>
									{generateInvoiceEudrLoading && <ButtonCircularProgress />} Generate Invoice
								</Button>
								<Button
									type="button"
									variant="contained"
									size="small"
									onClick={(e) => {
										e.stopPropagation();
										handleDownloadInvoice();
									}}
									startIcon={<CloudDownload sx={{ m: 0, p: 0 }} />}
									disabled={downloadEudrLastInvoiceLoading}
								>
									{downloadEudrLastInvoiceLoading && <ButtonCircularProgress />}
									Download Invoice
								</Button>

								<Button
									type="button"
									variant="contained"
									size="small"
									onClick={(e) => {
										e.stopPropagation();
										setOpenAddAttachmentDialog(true);
									}}
									startIcon={<CloudUpload sx={{ m: 0, p: 0 }} />}
									disabled={uploadEudrContractLoading}
								>
									{uploadEudrContractLoading && <ButtonCircularProgress />}
									Upload Contract
								</Button>
								<Button
									type="button"
									variant="contained"
									size="small"
									onClick={(e) => {
										e.stopPropagation();
										handleDownloadContract();
									}}
									startIcon={<CloudDownload sx={{ m: 0, p: 0 }} />}
								>
									Download Contract
								</Button>
								<Button
									type="button"
									variant="contained"
									size="small"
									onClick={(e) => {
										e.stopPropagation();
										handleOpenActivateAccountDialog();
									}}
									disabled={activateEudrLoading}
								>
									{activateEudrLoading && <ButtonCircularProgress />}
									Activate Account
								</Button>
							</Box>
						</Box>
					}
				/>
			)}
			{!eudrItems &&
				eudrItemsLoading &&
				new Array(4)
					.fill(0)
					.map((i) => <Skeleton key={i} variant="rectangular" height="25px" sx={{ mb: '25px' }} />)}

			<ConfirmationDialog
				open={openActivateAccountDialog}
				setClose={handleCloseActivateAccountDialog}
				handleConfirm={handleActivateAccount}
				title="Activate Account"
				text="Are you sure you want to activate the account?"
			/>
			<AddAttachmentDialog
				open={openAddAttachmentDialog}
				setClose={setCloseAddAttachmentDialog}
				type="EUDR contract"
			/>
		</Box>
	);
};
